/* eslint-disable no-nested-ternary */
import Skeleton from "@Components/Skeleton";
import { AppState } from "@Store/index";
import { useSelector, shallowEqual } from "react-redux";
import { getHeadingText, getRegionBreadcrumb } from "./constant";
import styles from "./SearchHeadingContent.module.scss";
import BreadCrumbs from "@Components/BreadCrumbs";
import getRegionHeading from "helpers/getRegionHeadings";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import { useEffect, useRef, useState } from "react";
import throttle from "@Utils/throttle";
import Button from "@Components/Button";
import ArrowUpIcon from "@Icons/ArrowUpIcon";
import config from "@Config/index";
import useTranslationHook from "hooks/useTranslationHook";
import useWhitelabel from "hooks/useWhitelabel";
import wrapperHOC from "@Utils/wrapperHOC";
import getUserLanguageCSR from "helpers/getUserLanguageCSR";
import useIsMiniProgram from "hooks/useIsMiniProgram";
import PropertyDetailsModal from "@Pages/PropertyPage/components/PropertyDetailsModal";
import Portal from "@Components/Portal";
import useIsApp from "hooks/useIsApp";
import { convertToWhitelabelURL } from "@Pages/WhitelabelHome/Desktop/helpers/WhiteLabelRedirectionHelper";
import replaceAmberDomainInLinks from "@Utils/clientUtils/dangerouslySetInnerHtmlUtility/replaceAmberDomainInLinks";
import stripBlogLinks from "@Utils/clientUtils/dangerouslySetInnerHtmlUtility/stripBlogLinks";
import WhiteLabelBreadCrumbs from "@Pages/WhitelabelHome/components/WhiteLabelBreadCrumbs/WhiteLabelBreadCrumbs";

interface SearchHeadingContentDesktopProps {
  hideBreadcrumb?: boolean;
}

const SearchHeadingContentDesktop = ({
  hideBreadcrumb = false,
}: SearchHeadingContentDesktopProps) => {
  const { readyStatus, region, meta, next, isDesktop, isInternal, filters } = useSelector(
    ({ searchPageData, appData }: AppState) => ({
      readyStatus: searchPageData.searchList.readyStatus,
      region: searchPageData.region,
      meta: searchPageData.searchList.meta,
      next: searchPageData.searchList.next,
      isDesktop: appData.isDesktop,
      isInternal: appData.isInternalUser,
      filters: searchPageData.filters,
    }),
    shallowEqual,
  );

  const [showBackToTopButton, setShowBackToTopButton] = useState(false);
  const prevScrollPos = useRef(0);
  const { t } = useTranslationHook();
  const headingText = getHeadingText(region, t);

  const showingText = t("searchpage.components.sectionheadingcontentdesktop.showing");
  const placesText = t("searchpage.components.sectionheadingcontentdesktop.places");
  const backToTopText = t("searchpage.components.sectionheadingcontentdesktop.backtotop");
  const userLanguage = getUserLanguageCSR();
  const rentalsText = t("searchpage.components.sectionheadingcontentdesktop.rentals");
  const isMiniProgram = useIsMiniProgram();
  const isApp = useIsApp();
  const [expanded, setExpanded] = useState(false);
  const summary = config.IS_CHINA_APP
    ? region?.description[0]?.value
    : region?.meta?.locality_summary || "";

  // Check if this is a nearby page
  // Use includes to match locality=near-by-{region-name} pattern
  const isNearbyPage = filters?.locality && String(filters.locality).includes("near-by");

  const handleBackToTopClick = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleScrollThrottled = throttle(() => {
    const currentScrollPos = window.pageYOffset;
    if (
      currentScrollPos > 1000 &&
      (prevScrollPos.current > currentScrollPos || currentScrollPos < 10)
    ) {
      setShowBackToTopButton(true);
    } else {
      setShowBackToTopButton(false);
    }
    prevScrollPos.current = currentScrollPos;
  }, 400);

  useEffect(() => {
    if (!region?.canonical_name) return;

    sessionStorage.setItem(`lastSearchUrl-${region.canonical_name}`, window.location.href);
  }, [region]);

  useEffect(() => {
    window.addEventListener("scroll", handleScrollThrottled);
    return () => {
      window.removeEventListener("scroll", handleScrollThrottled);
    };
  }, []);

  const { isWhitelabel, isDemoSubdomain, partner_uuid, isUniversityPartner, whitelabelConfig } =
    useWhitelabel();
  const regionHeading = getRegionHeading(region, true);
  // On Chinese site, keep the region/university name in English
  const displayRegionHeading =
    userLanguage === "zh" && region?.canonical_base_name
      ? region.canonical_base_name
          .split("-")
          .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ")
      : regionHeading;
  const breadcrumb = getRegionBreadcrumb(
    region,
    { parent_1: true },
    region?.location?.country?.short_name,
  );
  const shouldHideBreadcrumbs =
    isWhitelabel &&
    isUniversityPartner &&
    whitelabelConfig?.is_university_properties_section_enabled;

  if (readyStatus === "request" && (!next || next < 2)) {
    return (
      <div className={styles.containerDesktop}>
        <Skeleton width="20%" height="16px" mb="8px" />
        <Skeleton width="60%" height="24px" mb="8px" />
      </div>
    );
  }

  const modalHeading =
    config.IS_CHINA_APP && !isMiniProgram
      ? userLanguage === "zh"
        ? `${displayRegionHeading}${rentalsText}`
        : `${displayRegionHeading} ${rentalsText}`
      : `${headingText} ${displayRegionHeading}`;

  return (
    <>
      <div className={formatClassNames(styles.container, isDesktop ? styles.containerDesktop : "")}>
        {!hideBreadcrumb &&
          (isWhitelabel ? (
            <WhiteLabelBreadCrumbs
              pageType="search"
              regionName={region?.name}
              regionSlug={region?.canonical_name}
            />
          ) : (
            !shouldHideBreadcrumbs && (
              <BreadCrumbs
                data={breadcrumb.map(({ link, name }: any) => {
                  let breadcrumbLink = link?.toString();
                  if (isWhitelabel) {
                    breadcrumbLink = convertToWhitelabelURL(
                      breadcrumbLink,
                      isDemoSubdomain,
                      partner_uuid,
                    );
                  }
                  return {
                    link: breadcrumbLink,
                    name: `${name}${
                      config.IS_CHINA_APP
                        ? userLanguage === "zh"
                          ? rentalsText
                          : ` ${rentalsText}`
                        : ""
                    }`,
                  };
                })}
                dataTestId="searchpage-breadcrumb"
                className={isNearbyPage ? styles.nearbyBreadcrumbs : ""}
              />
            )
          ))}
        <div className={styles.headingWrap}>
          {isNearbyPage ? (
            <>
              {!isMiniProgram && (
                <span>
                  {showingText}&nbsp;
                  <strong data-testid="search-page-heading-numberOfProperties">
                    {meta?.count || 0}
                  </strong>
                  &nbsp;nearby properties
                </span>
              )}
            </>
          ) : (
            <>
              <h1 className={formatClassNames(isWhitelabel ? "wl-title-font" : "")}>
                {config?.IS_CHINA_APP && !isMiniProgram ? (
                  <>
                    <strong data-testid="search-page-region-heading">
                      {config.IS_CHINA_APP
                        ? userLanguage === "zh"
                          ? `${displayRegionHeading}${rentalsText}`
                          : `${displayRegionHeading} ${rentalsText}`
                        : displayRegionHeading}
                    </strong>
                    &nbsp;
                  </>
                ) : (
                  <>
                    {headingText}&nbsp;
                    <strong data-testid="search-page-region-heading">{displayRegionHeading}</strong>
                  </>
                )}
              </h1>
              {!isMiniProgram && (
                <span>
                  &nbsp; | &nbsp; {showingText}&nbsp;
                  <strong data-testid="search-page-heading-numberOfProperties">
                    {meta?.count || 0}
                  </strong>
                  &nbsp;{placesText}
                </span>
              )}
            </>
          )}
        </div>
        {summary &&
          !isApp &&
          !isMiniProgram &&
          region?.region_type === "locality" &&
          !isNearbyPage && (
            <div
              className={formatClassNames(
                styles.localitySummaryContainer,
                isInternal && styles.agentView,
                isDesktop && styles.desktop,
              )}
            >
              <div
                dangerouslySetInnerHTML={{
                  __html: stripBlogLinks(replaceAmberDomainInLinks(summary)),
                }}
              />

              <span onClick={() => setExpanded(true)} role="none">
                {t("searchpage.searchheadingcontent.readmore")}
              </span>
            </div>
          )}
      </div>
      {showBackToTopButton && (
        <Button
          LeftIcon={() => <ArrowUpIcon />}
          className={styles.backToTopButton}
          onClick={handleBackToTopClick}
          variant="none"
        >
          {backToTopText}
        </Button>
      )}
      {expanded && (
        <Portal rootId="forPortal">
          <PropertyDetailsModal
            header={modalHeading}
            hidePropertyModal={() => setExpanded(false)}
            isDesktop={isDesktop}
          >
            <div>
              <div
                className={styles.modalContent}
                dangerouslySetInnerHTML={{
                  __html: stripBlogLinks(replaceAmberDomainInLinks(summary)),
                }}
              />
            </div>
          </PropertyDetailsModal>
        </Portal>
      )}
    </>
  );
};

export default wrapperHOC(SearchHeadingContentDesktop, {
  componentName: "SearchHeadingContentDesktop-Search",
  showForChina: true,
});
